import { useMemo } from "react";
import { formatarNomeUpper, formatarNomeLower } from "../utils/formatadores";
import { obterPrioridadeProcesso } from "../utils/prioridadesProcessos";

const COLUNAS = [
  { chave: "numeroProcesso", titulo: "Número do Processo" },
  { chave: "autor", titulo: "Autor" },
  { chave: "advogadoId", titulo: "Advogado" },
  { chave: "areaDireito", titulo: "Área do Direito" },
  { chave: "faseAcao", titulo: "Fase da Ação" },
  { chave: "nomeNotificacao", titulo: "Tipo de Notificação" },
  { chave: "prazoFinal", titulo: "Prazo Final" },
];

const parseDataLocal = (dataStr) => {
  if (!dataStr) return null;
  if (dataStr instanceof Date) return dataStr;
  
  const partes = dataStr.split("T")[0].split("-");
  if (partes.length === 3) {
    const [ano, mes, dia] = partes.map(Number);
    return new Date(ano, mes - 1, dia);
  }
  return new Date(dataStr);
};

const useTabelaProcessos = (processos, usuarios) => {
  const mapaUsuarios = useMemo(() => {
    if (!usuarios) return {};
    return Object.fromEntries(
      usuarios.map((u) => [String(u._id || u.id), u])
    );
  }, [usuarios]);

  const linhas = useMemo(() => {
    if (!processos) return [];

    return processos.map((processo) => {
      const linha = {
        id: processo.id || processo._id,
        processoOriginal: processo,
        numeroProcesso: processo.numeroProcesso ?? "",
        autor: formatarNomeUpper(processo.autor),
        areaDireito: processo.areaDireito ?? "",
        faseAcao: processo.faseAcao ?? ""
      };

      const usuario = mapaUsuarios[String(processo.advogadoId)];
      linha.advogadoId = usuario?.nome
        ? formatarNomeUpper(usuario.nome)
        : "Não encontrado";

      const ultimaMovimentacao = processo.ultimaMovimentacao;
      const tipoEspecializado = formatarNomeLower(ultimaMovimentacao?.tipoEspecializado) || "";

      // 🟢 Declaração de prazos e resolvido posicionada ANTES do uso
      const prazos = ultimaMovimentacao?.prazos;
      const resolvido = prazos?.resolvido || false;
      const cumprido = Boolean(prazos?.cumprido ?? prazos?.resolvido ?? false);

      linha.nomeNotificacao = {
        texto: tipoEspecializado,
        urgencia: ultimaMovimentacao?.urgencia || "MEDIA",
        resolvido,
        cumprido
      };

      const dataBruta = prazos?.dataPrazo || prazos?.dataVencimento;
      const dataVencimentoEditada = prazos?.dataVencimentoEditada ?? false;

      if (!dataBruta) {
        linha.prazoFinal = {
          dataFormatada: "—",
          status: "sem-prazo",
          diasRestantes: null,
          resolvido: false,
          cumprido,
          editada: false,
        };
      } else {
        const dataPrazo = parseDataLocal(dataBruta);

        if (!dataPrazo || Number.isNaN(dataPrazo.getTime())) {
          linha.prazoFinal = {
            dataFormatada: "—",
            status: "sem-prazo",
            diasRestantes: null,
            resolvido: false,
            cumprido,
            editada: false,
          };
        } else {
          const hoje = new Date();
          hoje.setHours(0, 0, 0, 0);

          const dataPrazoComparacao = new Date(dataPrazo);
          dataPrazoComparacao.setHours(0, 0, 0, 0);

          const diffTempo = dataPrazoComparacao - hoje;
          const diasRestantes = Math.ceil(diffTempo / (1000 * 60 * 60 * 24));

          let status = "no-prazo";

          if (cumprido || resolvido) {
            status = "cumprido";
          } else if (diasRestantes < 0) {
            status = "vencido";
          } else if (diasRestantes === 0) {
            status = "hoje";
          } else if (diasRestantes <= 2) {
            status = "alerta";
          }

          const dataBase = dataPrazo.toLocaleDateString("pt-BR");
          const dataFormatada = dataVencimentoEditada ? dataBase : `${dataBase}*`;

          linha.prazoFinal = {
            dataFormatada,
            diasRestantes,
            status,
            resolvido,
            cumprido,
            editada: dataVencimentoEditada,
          };
        }
      }

      linha.prioridade = obterPrioridadeProcesso(processo);
      return linha;
    });
  }, [processos, mapaUsuarios]);

  return {
    colunas: COLUNAS,
    linhas
  };
};

export default useTabelaProcessos;